// Competency.jsx
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Check, ArrowRight, ChevronRight, Shield } from 'lucide-react';
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import './Competency.css';

const site = 'https://your-platform.com';

function FadeUp({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const levelColors = {
  entry: '#6b7280',
  trainee: '#3b82f6',
  junior: '#10b981',
  independent: '#f59e0b',
  senior: '#ef4444',
  principal: '#ff6b35',
};

export default function CompetencyPage() {
  const competency = {
    hero: {
      eyebrow: 'COMPETENCY & CERTIFICATION',
      headline: 'From Aspirant to Principal Authority — A Verified Journey.',
      subheadline:
        "Qualion's competency framework provides a structured, evidence-based pathway for every technical professional — from the first day of a graduate's career to the recognition of a principal authority."
    },
    framework_intro: {
      eyebrow: 'THE FRAMEWORK',
      headline: 'What Is the Qualion Competency Framework?',
      body: "The Qualion Competency Framework is a structured, industry-aligned system for defining, measuring, and validating technical competence across inspection and assurance disciplines. It replaces subjective assessment with objective, evidence-based progression — ensuring that every professional on the platform has demonstrated, not just claimed, their capabilities.",
      pillars: [
        {
          title: 'Evidence-Based',
          description: 'Every competency claim is backed by documented field evidence, mentor validation, and project records.'
        },
        {
          title: 'Industry-Aligned',
          description: 'Frameworks are calibrated to international inspection standards including CSWIP, API, ASNT, and ISO.'
        },
        {
          title: 'Portable & Verifiable',
          description: 'Competency records are portable across employers and verifiable by clients, regulators, and auditors.'
        }
      ]
    },
    levels: [
      {
        badge: 'L0',
        title: 'Aspirant',
        typical_profile: 'Fresh graduates, career changers, pre-employment candidates',
        color: 'entry',
        description: 'The starting point for graduates and career changers entering the technical inspection pathway.',
        requirements: [
          'Relevant engineering or technical degree or equivalent',
          'Registration on the Qualion platform',
          'Completion of platform orientation and framework induction',
          'Assignment of a registered Qualion mentor'
        ],
        capabilities: 'Theoretical knowledge of inspection principles. No independent field deployment.'
      },
      {
        badge: 'L1',
        title: 'Trainee Surveyor',
        typical_profile: 'Graduate trainees, apprentice inspectors, entry-career professionals',
        color: 'trainee',
        description: 'Active training phase with structured logbook, mentor oversight, and supervised field exposure.',
        requirements: [
          'Active digital logbook with minimum 3 months of entries',
          'Mentor-validated field activity records',
          'Completion of at least 2 competency units with evidence',
          'Attendance at structured training or CPD activities'
        ],
        capabilities: 'Supervised field activities. Logbook-documented learning. No independent inspection sign-off.'
      },
      {
        badge: 'L2',
        title: 'Junior Surveyor',
        typical_profile: 'Junior inspectors, newly certified professionals, early-project surveyors',
        color: 'junior',
        description: 'Demonstrated field competency with mentor-validated evidence across core inspection disciplines.',
        requirements: [
          'Minimum 12 months of active logbook records',
          'Mentor sign-off on at least 5 competency units',
          'Completion of at least one industry-recognised certification (e.g. CSWIP 3.1, API 510)',
          'Documented participation in at least 3 inspection projects'
        ],
        capabilities: 'Assisted inspection delivery. Can operate under supervision of L3+ surveyor.'
      },
      {
        badge: 'L3',
        title: 'Independent Surveyor',
        typical_profile: 'Experienced inspectors, independent surveyors, project-based professionals',
        color: 'independent',
        description: 'Capable of autonomous inspection delivery with full documentation and reporting responsibilities.',
        requirements: [
          'Minimum 3 years of documented field experience',
          'Full competency matrix completion for primary discipline',
          'At least 2 industry certifications relevant to scope',
          'Demonstrated independent report preparation and sign-off'
        ],
        capabilities: 'Independent inspection delivery. Full report sign-off. Can supervise L1-L2 surveyors.'
      },
      {
        badge: 'L4',
        title: 'Senior Surveyor',
        typical_profile: 'Senior inspectors, lead surveyors, inspection team leaders',
        color: 'senior',
        description: 'Leads inspection teams, mentors junior professionals, and manages complex multi-discipline deployments.',
        requirements: [
          'Minimum 7 years of documented field experience',
          'Full competency matrix completion across at least 2 disciplines',
          'Active mentor registration with validated trainee records',
          'Demonstrated project leadership and client management experience'
        ],
        capabilities: 'Team leadership. Multi-discipline inspection management. Formal mentoring responsibilities.'
      },
      {
        badge: 'L5',
        title: 'Principal / Authority',
        typical_profile: 'Principal inspectors, technical directors, industry fellows, chartered professionals',
        color: 'principal',
        description: 'Recognised technical authority with full competency portfolio, industry standing, and strategic advisory capability.',
        requirements: [
          'Minimum 12 years of documented field experience',
          'Full competency portfolio across multiple disciplines',
          'Industry recognition (chartered status, fellowship, or equivalent)',
          'Demonstrated contribution to standards, training, or industry development'
        ],
        capabilities: 'Technical authority sign-off. Strategic advisory. Industry standards contribution.'
      }
    ],
    certification_matrix: {
      headline: 'Recognised Certifications by Discipline',
      body: 'Qualion aligns with the leading international certification bodies across all supported inspection disciplines.',
      disciplines: [
        {
          name: 'Welding & NDT',
          certifications: [
            'CSWIP 3.1 / 3.2',
            'AWS CWI',
            'ASNT Level II / III',
            'PCN Level 2 / 3',
            'EN ISO 9712'
          ]
        },
        {
          name: 'Pressure Equipment',
          certifications: [
            'API 510 (Pressure Vessel)',
            'API 570 (Piping)',
            'API 653 (Storage Tanks)',
            'ASME Section V / VIII'
          ]
        },
        {
          name: 'Marine & Offshore',
          certifications: [
            'IACS Classification',
            "Lloyd's Register",
            'DNV GL',
            'Bureau Veritas',
            'ABS Certification'
          ]
        },
        {
          name: 'Civil & Structural',
          certifications: [
            'IStructE Membership',
            'ICE Membership',
            'CIOB Membership',
            'ACI Certification'
          ]
        },
        {
          name: 'Quality Management',
          certifications: [
            'ISO 9001 Lead Auditor',
            'CQI / IRCA Certified',
            'ASQ CQE / CQA',
            'IAQG OASIS Auditor'
          ]
        },
        {
          name: 'Health, Safety & Environment',
          certifications: [
            'NEBOSH IGC / Diploma',
            'IOSH Managing Safely',
            'ISO 45001 Lead Auditor',
            'BOSIET / HUET'
          ]
        }
      ]
    },
    passport_concept: {
      eyebrow: 'COMPETENCY PASSPORT',
      headline: 'The Qualion Competency Passport',
      body: 'A single, portable, verifiable record of everything a professional has demonstrated — not just claimed.',
      features: [
        {
          title: 'Verified Evidence Portfolio',
          description: 'Every competency claim is backed by documented evidence, mentor sign-off, and project records.'
        },
        {
          title: 'Certification Registry',
          description: 'All professional certifications stored with expiry tracking and verification links.'
        },
        {
          title: 'Project & Deployment History',
          description: 'A complete record of every project, client, and inspection scope the professional has worked on.'
        },
        {
          title: 'Mentor Endorsements',
          description: 'Formal endorsements from registered Qualion mentors at each competency milestone.'
        },
        {
          title: 'Portable & Shareable',
          description: 'Share your passport with clients, employers, or regulators — with controlled access and audit trail.'
        }
      ]
    },
    cta: {
      headline: 'Start Your Competency Journey Today',
      subheadline: 'Whether you are a graduate taking your first step or a senior professional building your legacy — Qualion has a pathway for you.',
      cta_primary: 'Register Interest →',
      cta_secondary: 'Explore the Platform →'
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${site}/competency#webpage`,
    name: 'Competency & Certification Framework | Professional Development Platform',
    url: `${site}/competency`,
    isPartOf: { '@id': `${site}/#website` },
    about: { '@id': `${site}/#organization` },
  };

  return (
    <>
      <Helmet>
        <title>Competency & Certification Framework | Professional Development Platform</title>
        <meta name="description" content="Explore our structured competency framework — L0 Aspirant to L5 Principal Authority. Evidence-based progression, industry-aligned certification matrix, and the Competency Passport." />
        <link rel="canonical" href={`${site}/competency`} />
        <meta property="og:title" content="Competency & Certification Framework | Professional Development Platform" />
        <meta property="og:description" content="From Aspirant to Principal Authority — a verified, evidence-based competency journey for professionals." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${site}/competency`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Navbar />

      <main>
        {/* ── HERO ── */}
        <section className="competency-hero">
          <div className="competency-hero-top-line" />
          <div className="competency-hero-grid-overlay" />
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
              className="competency-hero-content"
            >
              <div className="competency-hero-eyebrow">
                <div className="competency-hero-line" />
                <span>{competency.hero.eyebrow}</span>
              </div>
              <h1 className="competency-hero-title">{competency.hero.headline}</h1>
              <p className="competency-hero-subtitle">{competency.hero.subheadline}</p>
            </motion.div>
          </div>
        </section>

        {/* ── FRAMEWORK INTRO ── */}
        <section className="competency-framework-section">
          <div className="container">
            <div className="competency-framework-grid">
              <FadeUp className="competency-framework-content">
                <div className="competency-framework-eyebrow">
                  <div className="competency-framework-line" />
                  <span>{competency.framework_intro.eyebrow}</span>
                </div>
                <h2 className="competency-framework-title">{competency.framework_intro.headline}</h2>
                <p className="competency-framework-body">{competency.framework_intro.body}</p>
              </FadeUp>

              <FadeUp delay={0.15}>
                <div className="competency-pillars">
                  {competency.framework_intro.pillars.map((pillar, i) => (
                    <div key={i} className="competency-pillar">
                      <div className="competency-pillar-icon">
                        <Check size={14} />
                      </div>
                      <div>
                        <h3 className="competency-pillar-title">{pillar.title}</h3>
                        <p className="competency-pillar-description">{pillar.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </FadeUp>
            </div>
          </div>
        </section>

        {/* ── COMPETENCY LEVELS ── */}
        <section className="competency-levels-section">
          <div className="competency-levels-bg" />
          <div className="container">
            <FadeUp className="competency-levels-header">
              <div className="competency-levels-header-line">
                <div className="competency-hero-line" />
                <span>COMPETENCY LEVELS</span>
                <div className="competency-hero-line" />
              </div>
              <h2>Six Levels. One Continuous Journey.</h2>
              <p>Each level has defined requirements, capabilities, and evidence criteria — removing subjectivity from professional progression.</p>
            </FadeUp>

            <div className="competency-levels-list">
              {competency.levels.map((level, i) => {
                const color = levelColors[level.color] || '#ff6b35';
                return (
                  <FadeUp key={i} delay={i * 0.08}>
                    <div className="competency-level-item">
                      <div className="competency-level-grid">
                        {/* Badge */}
                        <div className="competency-level-badge-wrapper">
                          <div
                            className="competency-level-badge"
                            style={{ borderColor: color, backgroundColor: `${color}20` }}
                          >
                            <span style={{ color }}>{level.badge}</span>
                          </div>
                          <div className="competency-level-badge-mobile">
                            <h3>{level.title}</h3>
                            <p>{level.typical_profile}</p>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="competency-level-content">
                          <div className="competency-level-header-desktop">
                            <h3>{level.title}</h3>
                            <p>{level.typical_profile}</p>
                          </div>
                          <p className="competency-level-description">{level.description}</p>

                          <div className="competency-level-details">
                            <div>
                              <div className="competency-level-label">REQUIREMENTS</div>
                              <ul className="competency-level-requirements">
                                {level.requirements.map((req, j) => (
                                  <li key={j}>
                                    <div className="competency-level-dot" style={{ backgroundColor: color }} />
                                    <span>{req}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <div className="competency-level-label">CAPABILITIES</div>
                              <p className="competency-level-capabilities">{level.capabilities}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </FadeUp>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── CERTIFICATION MATRIX ── */}
        <section className="competency-certification-section">
          <div className="container">
            <FadeUp className="competency-certification-header">
              <div className="competency-levels-header-line">
                <div className="competency-hero-line" />
                <span>CERTIFICATION MATRIX</span>
                <div className="competency-hero-line" />
              </div>
              <h2>{competency.certification_matrix.headline}</h2>
              <p className="competency-certification-body">{competency.certification_matrix.body}</p>
            </FadeUp>

            <div className="competency-certification-grid">
              {competency.certification_matrix.disciplines.map((disc, i) => (
                <FadeUp key={i} delay={i * 0.07}>
                  <div className="competency-certification-card">
                    <h3>{disc.name}</h3>
                    <ul>
                      {disc.certifications.map((cert, j) => (
                        <li key={j}>
                          <div className="competency-cert-dot" />
                          <span>{cert}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        {/* ── COMPETENCY PASSPORT ── */}
        <section className="competency-passport-section">
          <div className="container">
            <div className="competency-passport-grid">
              <FadeUp>
                <div className="competency-passport-content">
                  <div className="competency-framework-eyebrow">
                    <div className="competency-framework-line" />
                    <span>{competency.passport_concept.eyebrow}</span>
                  </div>
                  <h2 className="competency-passport-title">{competency.passport_concept.headline}</h2>
                  <p className="competency-passport-body">{competency.passport_concept.body}</p>
                  <div className="competency-passport-features">
                    {competency.passport_concept.features.map((feat, i) => (
                      <div key={i} className="competency-passport-feature">
                        <div className="competency-passport-check">
                          <Check size={12} />
                        </div>
                        <div>
                          <span className="competency-passport-feature-title">{feat.title}</span>
                          <span className="competency-passport-feature-desc"> — {feat.description}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </FadeUp>

              <FadeUp delay={0.15}>
                <div className="competency-passport-card">
                  <div className="competency-passport-bg-blob" />
                  <div className="competency-passport-inner">
                    <div className="competency-passport-header">
                      <div>
                        <div className="competency-passport-brand">Qualion</div>
                        <div className="competency-passport-name">Competency Passport</div>
                      </div>
                      <div className="competency-passport-level">L4 — Senior</div>
                    </div>

                    <div className="competency-passport-user">
                      <div className="competency-passport-avatar">
                        <Shield size={24} />
                      </div>
                      <div>
                        <div className="competency-passport-user-name">Senior Inspection Surveyor</div>
                        <div className="competency-passport-user-title">Oil & Gas · Marine · Infrastructure</div>
                        <div className="competency-passport-user-id">Qualion ID: QSV-2024-04821</div>
                      </div>
                    </div>

                    <div className="competency-passport-stats">
                      <div className="competency-passport-stat">
                        <div className="competency-passport-stat-value">142</div>
                        <div className="competency-passport-stat-label">Evidence Items</div>
                      </div>
                      <div className="competency-passport-stat">
                        <div className="competency-passport-stat-value">28</div>
                        <div className="competency-passport-stat-label">Projects</div>
                      </div>
                      <div className="competency-passport-stat">
                        <div className="competency-passport-stat-value">9</div>
                        <div className="competency-passport-stat-label">Certifications</div>
                      </div>
                    </div>

                    <div className="competency-passport-badges">
                      {['CSWIP 3.2', 'API 510', 'ASNT L3', 'NEBOSH IGC', 'ISO 9001'].map((badge) => (
                        <span key={badge} className="competency-passport-badge">{badge}</span>
                      ))}
                    </div>

                    <div className="competency-passport-footer">
                      <span>Last validated: July 2026</span>
                      <span className="competency-passport-verified">Verified ✓</span>
                    </div>
                  </div>
                </div>
              </FadeUp>
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="competency-cta-section">
          <div className="competency-cta-top-line" />
          <div className="container">
            <FadeUp>
              <div className="competency-cta-content">
                <h2>{competency.cta.headline}</h2>
                <p>{competency.cta.subheadline}</p>
                <div className="competency-cta-buttons">
                  <a href="/join-ecosystem" className="competency-cta-primary">
                    {competency.cta.cta_primary}
                  </a>
                  <a href="/platform" className="competency-cta-secondary">
                    {competency.cta.cta_secondary}
                  </a>
                </div>
              </div>
            </FadeUp>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}